/**
 * Research-backed SEO guides for Special Services add-ons (Kochi).
 * Match order in matchAddonNameToGuideHref: most specific first.
 * Legacy bed-bug / ULV stay on dedicated static pages.
 */

export type GuideAccent = 'rose' | 'violet' | 'mint' | 'peach';

export interface ProductChip {
  label: string;
  name: string;
  detail: string;
}

export interface GuideSectionDef {
  title: string;
  tone?: 'white' | 'lavender' | 'mint';
  paragraphs?: string[];
  bullets?: string[];
  /** Numbered ordered steps (each may start with "Heading: detail" for bold heading) */
  steps?: string[];
  /** Highlighted callout box */
  noteBox?: { type: 'warning' | 'info' | 'tip'; text: string };
  /** Single 3M product highlight inside this section */
  productHighlight?: { label?: string; name: string; detail: string };
  /** Grid use-case cards */
  gridCards?: string[];
}

export interface SpecialServiceGuideDef {
  slug: string;
  navLabel: string;
  accent: GuideAccent;
  eyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  heroImageUrl: string;
  heroImageAlt: string;
  seo: { title: string; description: string; keywords: string };
  canonicalPath: string;
  schemaServiceName: string;
  /** Top product callout bar (array of chips) */
  productCallout?: ProductChip[];
  sections: GuideSectionDef[];
  faq: { q: string; a: string }[];
  /** AMC section text (shown after FAQ if present) */
  amcText?: string;
  /** Bottom CTA customisation */
  ctaTitle?: string;
  ctaBody?: string;
}

function hasWord(n: string, word: string): boolean {
  return new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(n);
}

/** All dynamic guides under /guides/:slug */
export const SPECIAL_SERVICE_GUIDES: SpecialServiceGuideDef[] = [
  // ─── 1. TOILET ACID WASH ─────────────────────────────────────────────────
  {
    slug: 'toilet-acid-wash-kochi',
    navLabel: 'Toilet acid wash & descaling',
    accent: 'mint',
    eyebrow: 'Special service · Bathrooms · Kochi',
    heroTitle: 'Toilet acid wash & heavy descaling',
    heroSubtitle:
      'Professional hard-water scale removal, stain dissolution, and surface restoration for WC bowls and bathroom fittings — using correct acid chemistry, dwell time, and thorough rinse protocols.',
    heroImageUrl: '/guide-toilet-acid-wash-tools-bubbles.png',
    heroImageAlt:
      'Indian professional cleaner from Tools & Bubbles in orange uniform — toilet and bathroom descaling service in Kochi',
    canonicalPath: '/guides/toilet-acid-wash-kochi',
    schemaServiceName: 'Toilet acid wash and bathroom descaling Kochi',
    seo: {
      title: 'Toilet Acid Wash Kochi | Descaling & Safety Guide | Tools & Bubbles',
      description:
        'Toilet acid wash and bathroom descaling in Kochi: when it helps, how we ventilate and rinse safely, and what to expect. Book via Special Services. Min {amountMinOrder}.',
      keywords:
        'toilet acid wash Kochi, bathroom descaling Ernakulam, WC scale removal, heavy toilet cleaning Kerala, Tools and Bubbles bathroom',
    },
    productCallout: [
      {
        label: 'Descaling agent',
        name: '3M™ Bathroom Disinfectant Cleaner / Citric acid-based descaler',
        detail:
          'Non-corrosive at use concentration · Safe for vitreous china & ceramic · Kills bathroom pathogens',
      },
      {
        label: 'Surface sanitiser',
        name: '3M P2 General Purpose Disinfectant',
        detail: 'BKC 2.25% · pH Neutral · Applied post-rinse for germ-kill assurance',
      },
    ],
    sections: [
      {
        title: 'Why a toilet may need acid washing',
        paragraphs: [
          "Kerala's groundwater is moderately hard in many areas of Kochi and Ernakulam. Over months, calcium carbonate and magnesium deposits build up as a white or yellow scale inside WC bowls, under rims, in the trap, and around faucet bases. Ordinary neutral or alkaline cleaners dissolve organic soil but cannot break down mineral scale.",
          'An acid wash (using a controlled-strength acidic solution) dissolves inorganic mineral scale chemically — restoring the internal glaze of the ceramic and improving flushing efficiency. It is also useful before sanitisation, property handover, or when a toilet has been unused for a prolonged period.',
        ],
      },
      {
        title: 'How we perform the service',
        steps: [
          'Assessment: Supervisor inspects the bowl, rim jets, trap, and adjacent tiles. Confirms fixture material compatibility (standard vitreous china — yes; decorative metal inlays or natural stone insets — adjusted method).',
          'Ventilation setup: Bathroom exhaust fan is turned on; window opened if present. Technician dons acid-resistant gloves, eye protection, and apron.',
          'Product application: Acid descaler is applied inside the bowl and under the rim using a squeeze bottle. Product is allowed to dwell 10–20 minutes depending on severity. NOT used on chrome fittings or brass in their path.',
          'Scrubbing: After dwell time, technician scrubs with a purpose-built toilet brush — targeting the waterline, under-rim jets, and trap inlet.',
          'Flush and rinse: Full flush and manual rinse ensures no acid residual remains in the bowl or trap. A neutralising rinse with bicarbonate solution may be applied for heavy treatments.',
          'Surface disinfection: 3M P2 disinfectant applied to the bowl, seat, lid, and adjacent hard surfaces for pathogen reduction. Contact time observed before final wipe.',
          'Inspection & documentation: Supervisor confirms scale removal and documents any existing cracks or damage that pre-existed treatment.',
        ],
      },
      {
        title: 'Before we arrive — your preparation',
        bullets: [
          'Ensure the bathroom is ventilated — window open or exhaust fan working.',
          'Remove all personal toiletries, soap dishes, toothbrushes, and towels from the bathroom.',
          'Tell us if the toilet is a pressure-flush, rimless design, or has a Washlet (electronic seat) — these may require a modified approach.',
          'Keep children and pets out of the bathroom throughout the service.',
          'Notify us of any known cracks in the bowl, loose fittings, or previous DIY treatments.',
        ],
        noteBox: {
          type: 'warning',
          text: 'Do not mix household bleach with any acid product at any point — this produces toxic chlorine gas. If you have recently used bleach in the toilet, tell us before service begins.',
        },
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Keep children and pets out of the bathroom during treatment and for 30 minutes after the final rinse. Acid products are corrosive to mucous membranes on contact.',
        },
        bullets: [
          'Do not allow children near open acid containers or the toilet bowl during treatment.',
          'After service the toilet is safe to use by all family members once rinsed — no re-entry restriction beyond 30 minutes.',
          'If a child accidentally touches the bowl before final rinse, wash the area with running water for 5 minutes and seek medical advice if irritation persists.',
          'All products are stored and transported securely; none are left on site after service.',
        ],
      },
      {
        title: 'After service care',
        tone: 'mint',
        bullets: [
          'The toilet is ready for normal use within 30–60 minutes of completing the service.',
          'Restored ceramic surfaces may appear noticeably brighter — this is expected.',
          'To maintain results: use a mild limescale preventer weekly and avoid leaving bleach sitting in the bowl for extended periods.',
          'Severe scale may require a second session after 3–4 weeks if the first round softened but did not fully dissolve all deposits.',
        ],
      },
    ],
    faq: [
      {
        q: 'How often should I book an acid wash?',
        a: 'For homes with hard water, once every 6–12 months prevents heavy scale build-up. A visible yellow or brown ring at the waterline is a reliable indicator.',
      },
      {
        q: 'Is acid washing safe for all toilet types?',
        a: 'Standard vitreous china is safe. We adjust for coloured bowls, rimless designs, and natural stone surrounds. Electronic Washlet seats are removed or protected before acid application.',
      },
      {
        q: 'Can you descale bathroom tiles and fixtures at the same time?',
        a: 'Yes — tile descaling, faucet limescale removal, and glass squeegee cleaning can all be added under the same bathroom service booking.',
      },
    ],
    amcText:
      'Quarterly bathroom deep cleans under our AMC prevent scale from becoming severe, saving on costly acid treatments. AMC customers receive priority scheduling and 15–20% savings versus one-off bookings. Available for homes and offices across Kochi.',
  },

  // ─── 2. WATER TANK & DRAINAGE ────────────────────────────────────────────
  {
    slug: 'water-tank-drainage-cleaning-kochi',
    navLabel: 'Water tank & drainage cleaning',
    accent: 'peach',
    eyebrow: 'Special service · Water & drains · Kochi',
    heroTitle: 'Overhead tank, sump & drainage cleaning',
    heroSubtitle:
      'Professional sludge removal, scrubbing, and certified disinfection rinse for overhead and underground water tanks — plus drain line clearing to eliminate odours and slow drainage.',
    heroImageUrl: '/guide-water-tank-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles technician in orange uniform — overhead water tank cleaning on a Kerala apartment rooftop',
    canonicalPath: '/guides/water-tank-drainage-cleaning-kochi',
    schemaServiceName: 'Water tank and drainage cleaning Kochi',
    seo: {
      title: 'Water Tank Cleaning Kochi | Sump & Drainage | Tools & Bubbles',
      description:
        'Overhead and underground water tank cleaning in Kochi — sludge removal, scrubbing, disinfection rinse, drainage flushing. Safety guide and AMC options. Book 9995216660.',
      keywords:
        'water tank cleaning Kochi, overhead tank cleaning Ernakulam, sump cleaning Kerala, drainage cleaning home Kochi, Tools and Bubbles water tank',
    },
    productCallout: [
      {
        label: 'Disinfection rinse',
        name: '3M P2 General Purpose Disinfectant (diluted)',
        detail:
          'BKC 2.25% · pH Neutral · Applied to tank interior post-scrub · Final clean-water flush before refill',
      },
      {
        label: 'Drain treatment',
        name: 'Enzymatic drain deodoriser (professional grade)',
        detail:
          'Biological enzyme formula · Breaks organic deposits in lines · Reduces odour at drain outlets',
      },
    ],
    sections: [
      {
        title: 'Why water tanks need regular cleaning',
        paragraphs: [
          "Kochi's water supply carries fine sediment and organic matter that settle to the bottom of overhead and underground tanks over time. The settled sludge provides nutrients for bacteria and algae, which can affect water colour, taste, and — in severe cases — safety.",
          'Kerala health and water authorities recommend cleaning water storage tanks every 6–12 months. In areas with harder water, increased turbidity, or following a burst pipe event, more frequent cleaning is advisable.',
          'Clean tanks also extend the life of submersible pumps, ball valves, and supply pipework by reducing sediment-driven wear.',
        ],
      },
      {
        title: 'Our cleaning process',
        steps: [
          'Isolation & drainage: We isolate the incoming supply, then drain remaining stored water safely (usually to drain, not into gardens given sediment content).',
          'Manual sludge removal: Technicians enter (for sumps >1,000 L with safe access) or use tools to scoop and vacuum sludge deposits from the floor of the tank.',
          'High-pressure scrubbing: Internal walls, floor, inlet pipe area, and overflow pipe area are scrubbed with Scotch-Brite professional pads and a pressure rinse where accessible.',
          'Disinfection rinse: 3M P2 diluted per professional guidance is applied to all internal surfaces. Contact time is observed (10 minutes minimum).',
          'Clean-water flush & refill: Final flush with clean water ensures no disinfectant residual before the tank is refilled for use.',
          'Drainage clearing (if booked): Accessible drain traps, floor traps, and kitchen/bathroom lines are cleared with mechanical tools and enzymatic product flushed through.',
          'Documentation: Our team photographs key findings (sludge level, visible corrosion, damage) and provides a written report.',
        ],
      },
      {
        title: 'Before we arrive — your checklist',
        bullets: [
          'Plan for no water supply to the premises during service — typically 3–5 hours.',
          'For overhead tanks: ensure roof access is clear, clean, and safe. Remove padlocks from inspection covers.',
          'For sumps: locate the sump inspection cover and ensure it is accessible.',
          'Notify us of tank capacity, material (concrete, PVC, FRP), and age of tank — this helps us plan the right tools.',
          'Inform neighbours who share a supply line if our draining may affect their supply temporarily.',
          'Keep children away from roof access or sump area during service.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Keep children away from the tank access point, roof, and drainage areas during service. A disinfection rinse is used inside the tank — allow a full clean-water flush and 60-minute waiting period before using water from the tank.',
        },
        bullets: [
          'After service flush: run taps in the kitchen and bathroom for 2–3 minutes to clear any remaining disinfectant from the supply lines before drinking or cooking.',
          'For infants and formula preparation, use bottled water for 24 hours after a tank clean as a precautionary measure.',
          'All technicians are trained in working safely in and around confined spaces per IS 10386 guidelines.',
        ],
      },
      {
        title: 'After service',
        tone: 'mint',
        bullets: [
          'Run each tap for 2–3 minutes after refilling to flush disinfectant residual from supply pipes.',
          'Water clarity may appear slightly different for 1–2 hours as the system settles — this is normal.',
          'Do not use water for cooking or drinking until the post-flush cycle is complete (approximately 1–2 hours after refill).',
          'A slight disinfectant smell at taps for the first 30–60 minutes is normal and not a safety concern.',
        ],
      },
      {
        title: 'International standards followed',
        bullets: [
          'WHO Guidelines for Drinking-water Quality — recommendations for household water storage hygiene.',
          'BIS IS 13428 — Indian Standard for glass-fibre reinforced plastic (FRP) tanks and maintenance.',
          'BIS IS 12288 — General requirements for water storage maintenance.',
          'Confined space working procedures aligned with IS 10386 for technician safety during sump entry.',
          '3M product dilution and application per official 3M India professional cleaning guidance.',
        ],
      },
    ],
    faq: [
      {
        q: 'How often should a home water tank be cleaned?',
        a: 'Every 6–12 months for most homes. If you notice sediment at taps, odour, or visible discolouration, clean sooner. Post-flood or post-contamination events require immediate cleaning.',
      },
      {
        q: 'Can you clean a concrete underground sump?',
        a: 'Yes. Concrete sumps are common in Kerala villas. We use appropriate scrubbing tools and ensure access safety. Very small (under 500 L) or structurally compromised sumps may need assessment first.',
      },
      {
        q: 'Do you also service drainage pipes under the floor?',
        a: 'We clear accessible drain traps, floor-level sumps, and kitchen/bathroom drain lines. Collapsed pipes, root-blocked underground mains, or sealed drain runs need a licensed plumber — we flag these on site.',
      },
    ],
    amcText:
      'Our Water Tank Cleaning AMC covers twice-yearly (or quarterly) scheduled cleans, priority call-out for contamination events, and a combined drainage service visit. Particularly popular for apartments with shared overhead tanks and villas in Kakkanad, Edappally, and Kaloor.',
  },

  // ─── 3. FLOOR SCRUBBING ──────────────────────────────────────────────────
  {
    slug: 'floor-scrubbing-machine-kochi',
    navLabel: 'Floor scrubbing & machine cleaning',
    accent: 'violet',
    eyebrow: 'Special service · Hard floors · Kochi',
    heroTitle: 'Machine floor scrubbing & deep tile cleaning',
    heroSubtitle:
      'Professional single-disc scrubber agitation removes ingrained grime from tile, granite, and marble — with the right pad, chemistry, and drying approach for Kerala homes and offices.',
    heroImageUrl: '/floor-scrubbing-machine-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles cleaner in orange uniform operating a single-disc floor scrubber on vitrified tiles — Kochi home',
    canonicalPath: '/guides/floor-scrubbing-machine-kochi',
    schemaServiceName: 'Floor scrubbing and machine deep cleaning Kochi',
    seo: {
      title: 'Floor Scrubbing Machine Cleaning Kochi | Tile & Granite | Tools & Bubbles',
      description:
        'Machine floor scrubbing for homes and offices in Kochi. Deep grout cleaning, tile restoration, granite polishing prep. Safety, drying, and slip prevention guide.',
      keywords:
        'floor scrubbing Kochi, disk machine cleaning Ernakulam, tile deep clean Kerala, floor machine cleaning Kochi, grout cleaning Kakkanad, Tools and Bubbles floor',
    },
    productCallout: [
      {
        label: 'Floor cleaning chemistry',
        name: '3M™ Neutral Cleaner Concentrate 3 / 3M™ 3-in-1 Floor Cleaner',
        detail:
          'pH-neutral · No rinse required · Safe on ceramic, vitrified tile, natural stone · Used with Scotch-Brite floor pads',
      },
      {
        label: 'Pad system',
        name: 'Scotch-Brite™ Professional Floor Pads (3M India)',
        detail:
          'Colour-coded: tan pads for polish, red for scrub, black for stripping. Matched to floor type.',
      },
    ],
    sections: [
      {
        title: 'Why machine scrubbing outperforms mopping',
        paragraphs: [
          'Conventional mopping redistributes soil suspended in water across the floor. A single-disc floor scrubbing machine combines mechanical agitation (pad rotation) with chemistry (3M Neutral Cleaner) and simultaneous water application to lift embedded grime from grout lines, textured surfaces, and microscopic surface scratches.',
          'After Kerala monsoon seasons, mud and silica deposits become impacted into tile and grout. A single machine scrub session removes what years of mopping has built up, restoring surface brightness and reducing bacterial harbourage in grout channels.',
        ],
      },
      {
        title: 'How we perform the service',
        steps: [
          'Assessment: Supervisor identifies floor type (glazed tile, vitrified tile, granite, marble, kota, concrete) and checks grout condition, any loose tiles, and existing damage.',
          'Furniture clearing: Small furniture and mats moved; fragile items stored safely.',
          'Dry vacuum sweep: All dry particulate removed before wet scrubbing to prevent abrasive scratching.',
          'Pre-treatment on grout: For heavily stained grout, 3M Neutral Cleaner is applied as a pre-spray and allowed to dwell 5 minutes.',
          'Machine scrubbing: Single-disc machine with appropriate Scotch-Brite pad makes multiple passes at low-to-medium speed. Edge areas finished by hand with matching chemistry.',
          'Wet pick-up / extraction: Floor is squeegeed and mopped dry to prevent streaking. For large areas, a wet-dry vacuum picks up dirty water immediately.',
          'Final rinse and inspection: Clean water rinse ensures no chemistry residual. Supervisor inspects grout lines and edge areas under torch light before sign-off.',
        ],
      },
      {
        title: 'Floor type guide — what we can and cannot treat',
        bullets: [
          'Glazed ceramic tile: Full scrubbing, aggressive pads acceptable, grout cleaning with mild acid if stained.',
          'Vitrified tile: Neutral cleaner, softer pads, no acid — glazing is not porous but scratches easily with abrasives.',
          'Polished granite: Neutral cleaner only, soft pad, speed controlled — do not use acid on polished granite.',
          'Honed marble: Extra care with softest pad; will show water marks if dried without buffing.',
          'Kota stone: Scrubbing effective; may need pH-appropriate stone cleaner for wax build-up.',
          'Cement / concrete: Can be deep-scrubbed; if previously coated with epoxy or wax, strip-and-redo may be needed.',
          'Waxed or sealed floors: Inform us — machine can strip wax; re-sealing after is a separate service.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Clear the floor of small furniture, mats, shoes, and objects on the ground.',
          'Tell us if the floor has underfloor heating — this affects water temperature and drying.',
          'Mention any loose tiles, significant cracks, or previous slip-coating applications.',
          'Ensure power points are accessible for the scrubbing machine.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Floors are wet and potentially slippery during and after machine scrubbing. Keep children and elderly family members off treated areas until fully dry.',
        },
        bullets: [
          'Drying time is typically 15–30 minutes after the final dry-pass, depending on ventilation and humidity.',
          '3M Neutral Cleaner is non-toxic at use dilution — safe when floor is dry. No residue or fume concern.',
          'Place slip-warning signs in common areas when treating high-traffic corridors.',
        ],
      },
      {
        title: 'After service',
        tone: 'mint',
        bullets: [
          'Allow full drying before resuming heavy foot traffic — typically 30 minutes.',
          'Grout lines will appear visibly lighter and more uniform after deep scrubbing.',
          'Consider a grout sealer application 24–48 hours after scrubbing to prevent rapid re-soiling.',
          'Machine scrubbing once or twice a year is sufficient for most Kerala homes; high-traffic commercial floors may benefit from quarterly treatment.',
        ],
      },
    ],
    faq: [
      {
        q: 'Will machine scrubbing damage my marble floor?',
        a: 'We use the appropriate speed, pressure, and pad hardness for marble. Standard machine scrubbing will not damage well-laid marble if neutral chemistry is used. Etched or extremely soft marble may need hand-polishing — we flag this on inspection.',
      },
      {
        q: 'Can you do just one room?',
        a: 'Yes. Machine floor scrubbing can cover a single room, bathroom, kitchen, or corridor. Minimum order rules apply for Special Services ({amountMinOrder} total). Combining with other add-ons helps reach the minimum efficiently.',
      },
    ],
    amcText:
      'Twice-yearly machine floor scrubbing under our AMC keeps grout from becoming deeply stained and reduces annual restoration costs. AMC customers receive 15–20% savings, priority scheduling during festivals, and a free inspection visit if they observe floor issues between scheduled dates.',
  },

  // ─── 4. CARPET SHAMPOO ───────────────────────────────────────────────────
  {
    slug: 'carpet-shampoo-service-kochi',
    navLabel: 'Carpet shampoo & extraction',
    accent: 'rose',
    eyebrow: 'Special service · Soft flooring · Kochi',
    heroTitle: 'Carpet & rug shampooing — deep extraction clean',
    heroSubtitle:
      "Professional hot-water extraction or low-moisture shampooing matched to fibre type, with controlled drying guidance for Kerala's humidity — international IICRC-aligned practices.",
    heroImageUrl: '/guide-carpet-shampoo-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles technician in orange uniform — carpet extraction and shampoo service in Kochi',
    canonicalPath: '/guides/carpet-shampoo-service-kochi',
    schemaServiceName: 'Carpet shampoo and extraction cleaning Kochi',
    seo: {
      title: 'Carpet Shampoo Kochi | Rug & Upholstery Cleaning | Tools & Bubbles',
      description:
        'Carpet and area rug shampooing in Kochi — fibre testing, hot-water extraction, stain guide, drying times. 3M Scotchgard protection available. Book via Special Services.',
      keywords:
        'carpet shampoo Kochi, rug cleaning Ernakulam, carpet extraction Kerala, carpet shampooing Kakkanad, area rug cleaning Kochi, Tools and Bubbles carpet',
    },
    productCallout: [
      {
        label: 'Primary cleaning chemistry',
        name: '3M™ Scotchgard™ Pretreatment Cleaner Concentrate 28',
        detail: 'Heavy-duty low-foam pre-spray for heavily soiled areas · Applied before extraction',
      },
      {
        label: 'Post-clean protector (optional)',
        name: '3M™ Scotchgard™ Carpet & Upholstery Protector Concentrate',
        detail: 'Invisible barrier against future soil and spills · Applied to damp carpet after cleaning',
      },
    ],
    sections: [
      {
        title: 'Why professional carpet cleaning matters',
        paragraphs: [
          "Kerala's monsoon season drives humidity into soft furnishings, accelerating dust mite growth and mould development in carpet backing. Regular vacuuming removes surface soil but cannot extract allergens, dust mite colonies, or deeply embedded soil from pile — these require professional wet extraction.",
          'Professional carpet cleaning following IICRC (Institute of Inspection, Cleaning and Restoration Certification) principles combines pH-appropriate chemistry with hot water or low-moisture agitation, then extracts dissolved soil and moisture systematically.',
          "We assess your carpet's fibre type, construction, and current soiling before selecting a method — hot water extraction for synthetics and many wool blends, low-moisture encapsulation for delicate fibres or quick-dry situations.",
        ],
      },
      {
        title: 'Our cleaning process — step by step',
        steps: [
          'Fibre identification & colour test: Technician identifies carpet fibre (nylon, polyester, wool, jute, polypropylene) and performs a colour fastness test in a concealed area.',
          'Dry vacuum: Heavy-duty vacuum removes loose dry soil and pet hair before wet cleaning — this is critical for effective extraction.',
          'Spot pre-treatment: Stubborn stains (grease, pet, ink, food) are pre-treated with appropriate 3M Scotchgard Pretreatment Cleaner or specialist stain remover. Dwell time: 3–5 minutes.',
          'Shampoo or extraction: Hot water extraction machine injects cleaning solution and simultaneously extracts — removes 95%+ of moisture from the pile. For delicate fibres, a low-moisture rotary pad method is used instead.',
          'Edge and stair detailing: Edges, stairs, and corners are hand-cleaned with a bonnet or small pad.',
          'Speed dry pass: Final dry pass with extraction removes maximum residual moisture to accelerate drying in humid Kerala conditions.',
          'Scotchgard application (if selected): 3M Scotchgard Protector concentrate (1:8 dilution) applied to damp carpet and allowed to dry — protects fibres for 12–18 months.',
        ],
      },
      {
        title: 'About stain expectations',
        tone: 'lavender',
        noteBox: {
          type: 'info',
          text: 'Some stains are permanent: dye bleach, pet urine that has oxidised and set, or older red wine and curry stains may lighten but not fully disappear. We set honest expectations during the pre-treatment inspection — not after cleaning has begun.',
        },
        bullets: [
          'Fresh stains respond best — always blot (never rub) and contact us for stain advice before it sets.',
          'Certain wool carpets may show texture change (pile distortion) from excessive moisture — low-moisture method used for these.',
          'White and cream carpets may show browning if over-wetted — our technique controls moisture to prevent this.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Vacuum the carpet yourself if possible — this improves our cleaning effectiveness.',
          'Remove small furniture, potted plants, and objects from the carpet area.',
          'Tell us about pet soiling, food spills, or any previous cleaning products used.',
          'For stairs: keep the staircase clear and pets/children away during cleaning.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Wet carpets are a slip hazard. Keep children and elderly off cleaned areas until fully dry. 3M cleaning products are non-toxic at use concentration once dry.',
        },
        bullets: [
          '3M Scotchgard Pretreatment Cleaner is rinse-free safe — no residue concern once dry.',
          '3M Scotchgard Protector is safe for children and pets once fully dry (typically 2–3 hours).',
          'Children with dust mite allergies benefit most from professional carpet extraction — book before school term begins for best results.',
          'Ensure room is ventilated with windows open during and after cleaning to accelerate drying.',
        ],
      },
      {
        title: 'After service — drying and care',
        tone: 'mint',
        bullets: [
          'Drying time: typically 2–4 hours in ventilated AC rooms; 4–8 hours in humid monsoon conditions without AC.',
          'Run fans on the carpet surface or use AC on dry/dehumidify mode to accelerate drying.',
          'Keep foot traffic minimal for 3–4 hours; place aluminium foil or plastic sheets under furniture legs before placing back.',
          'Once dry, vacuum lightly to restore pile direction.',
          'Carpet cleaned with Scotchgard protection repels new spills — wipe up liquids immediately rather than allowing them to soak in.',
        ],
      },
    ],
    faq: [
      {
        q: 'How long does carpet cleaning take?',
        a: 'Typically 30–45 minutes per room for a standard hot-water extraction clean. Heavily soiled carpets or those requiring spot pre-treatment take longer.',
      },
      {
        q: 'Can you clean a Persian or antique rug?',
        a: 'Antique and hand-knotted rugs need specialist care. We assess dye stability and fibre type — if the rug is valuable or delicate, we may recommend dry cleaning or specialist rug cleaning rather than on-site shampooing.',
      },
      {
        q: 'How often should carpets be professionally cleaned?',
        a: 'IICRC recommends every 12–18 months for residential carpets, every 6 months in homes with pets, children, or allergy sufferers, and every 3 months for commercial environments.',
      },
    ],
    amcText:
      'Our Carpet Care AMC schedules professional cleaning every 6–12 months, with interim spot-cleaning visits if needed. Homes in Kochi with children, pets, or allergy concerns benefit from regular scheduled service. Scotchgard protection reapplication is included in AMC plans.',
  },

  // ─── 5. MATTRESS CLEANING ────────────────────────────────────────────────
  {
    slug: 'mattress-cleaning-sanitization-kochi',
    navLabel: 'Mattress cleaning & hygiene',
    accent: 'mint',
    eyebrow: 'Special service · Sleep hygiene · Kochi',
    heroTitle: 'Mattress deep clean & allergen reduction',
    heroSubtitle:
      'Dust-mite vacuuming, stain treatment, low-moisture extraction, and 3M Scotchgard protection for your mattress — following UK NHS and IICRC-aligned hygiene practices.',
    heroImageUrl: '/guide-mattress-cleaning-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles cleaner in orange uniform — professional mattress vacuum and hygiene service Kochi',
    canonicalPath: '/guides/mattress-cleaning-sanitization-kochi',
    schemaServiceName: 'Mattress cleaning and hygiene service Kochi',
    seo: {
      title: 'Mattress Cleaning Kochi | Dust Mite & Stain Care | Tools & Bubbles',
      description:
        'Professional mattress cleaning in Kochi: dust mite removal, stain treatment, 3M Scotchgard protection, and safe drying. Allergy-friendly service. Book via Special Services.',
      keywords:
        'mattress cleaning Kochi, mattress shampoo Ernakulam, dust mite mattress Kerala, mattress sanitization Kochi, mattress deep clean Kakkanad, Tools and Bubbles mattress',
    },
    productCallout: [
      {
        label: 'Mattress cleaner',
        name: '3M™ Scotchgard™ Spot Remover and Upholstery Cleaner',
        detail:
          'Removes oil- and water-based stains · No rinse required · Safe on fabric, memory foam, latex',
      },
      {
        label: 'Protector (optional)',
        name: '3M™ Scotchgard™ Fabric Protector 4106',
        detail: 'Invisible barrier against liquid penetration · Extends mattress life · Safe when dry',
      },
    ],
    sections: [
      {
        title: 'What lives in your mattress — and why it matters',
        paragraphs: [
          "An unserviced mattress in a humid environment like Kochi can harbour up to 2 million dust mites in its inner layers. Their waste particles are a leading trigger of asthma, rhinitis, and eczema — particularly in children. Kerala's high humidity (60–90% for much of the year) creates ideal conditions for mite proliferation.",
          "We don't make medical claims, but the evidence base for mattress cleaning's positive effect on allergy sufferers is well-established in studies from the UK NHS and the American Academy of Allergy, Asthma & Immunology. Professional hot-air vacuuming and extraction reduces the surface and near-surface mite load significantly.",
          'The mattress also absorbs sweat (an average of 1 litre per person per night), body oils, dead skin cells, and occasional fluid spills — all of which create a substrate for bacteria and odour if not periodically addressed.',
        ],
      },
      {
        title: 'Our mattress cleaning process',
        steps: [
          'Mattress assessment: Type identified (innerspring, memory foam, latex, hybrid). Cover condition, existing stains, and signs of bed bug or mould checked. (Bed bug treatment is a separate specialist service.)',
          'Strip and inspection: All bedding removed before the team arrives (your prep). Surface inspected for stains and damage.',
          'HEPA-filter vacuuming: Professional-grade vacuum with HEPA filtration vacuums all surfaces, sides, seams, and handle areas — targeting surface mites and allergens.',
          'Stain pre-treatment: 3M Scotchgard Spot Remover applied to visible stains (sweat rings, food, fluid) with targeted dwell time of 2–5 minutes.',
          'Low-moisture extraction / upholstery cleaning: Light moisture application and extraction removes dissolved soil. Memory foam is treated with minimal moisture to protect foam structure.',
          'Deodorisation: Natural enzyme-based deodoriser applied to neutralise biological odours (sweat, pet, urine traces).',
          'Scotchgard protection (optional): 3M Scotchgard Fabric Protector sprayed uniformly and allowed to dry completely before bedding is replaced.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Strip all bedding, pillows, and mattress protectors — wash on a 60°C hot cycle if possible.',
          'Remove headboard cushions or bolsters from the bed area.',
          'Ensure the mattress can be accessed from all sides — move the bed slightly from the wall if possible.',
          'Inform us of mattress type, especially memory foam or latex, as moisture levels need adjustment.',
          'Mention any history of bed bugs, mould, or heavy fluid staining — these may require the dedicated bed bug treatment or mould specialist service instead.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Do not allow children to use the mattress until it is fully dry. Placing any bedding on a damp mattress can trap moisture and encourage mould within the foam layers.',
        },
        bullets: [
          '3M Scotchgard products are safe for children once fully dry (typically 2–4 hours).',
          'Allergy-prone children: a clean mattress with a dust-mite-proof encasement installed afterward provides the most effective protection.',
          'Do not allow children to put mouth to mattress fabric during the service.',
          'For infants: air cot mattresses in strong sunlight additionally after professional cleaning.',
        ],
      },
      {
        title: 'After service — drying and care',
        tone: 'mint',
        bullets: [
          'Allow 3–6 hours drying time with windows open and fan or AC running.',
          'Do not replace bedding until mattress surface is fully dry to touch across all areas.',
          'Place mattress in direct sunlight for 2–3 hours after drying (if practical) — UV provides additional surface sanitisation.',
          'Fit a dust-mite-proof mattress encasement immediately after service to lock in cleanliness.',
          'Regular professional cleaning every 6–12 months maintains allergen reduction.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can both sides of the mattress be cleaned?',
        a: 'Yes, we clean both sides if the mattress is double-sided (flip type). Single-sided mattresses (most modern foam hybrids) are cleaned on the sleeping surface and sides.',
      },
      {
        q: 'Will cleaning remove yellow sweat stains?',
        a: 'Most fresh to moderately old sweat stains respond well to 3M Scotchgard pre-treatment. Very old oxidised stains may only lighten, not fully disappear. We set expectations during assessment.',
      },
      {
        q: 'Is this the same as bed bug treatment?',
        a: 'No. Professional mattress cleaning removes surface soiling, allergens, and dust mites. It is not an insecticidal treatment for bed bugs. If you suspect bed bugs, book our dedicated Bed Bug Treatment service (see /bed-bug-treatment-kochi).',
      },
    ],
    amcText:
      'Our Mattress Hygiene AMC schedules professional cleaning every 6 months — ideal for children with asthma or eczema, elderly household members, or homes with pets. Included in our comprehensive Home Hygiene AMC packages available across Kochi.',
  },

  // ─── 6. SOFA / UPHOLSTERY ────────────────────────────────────────────────
  {
    slug: 'sofa-upholstery-shampoo-kochi',
    navLabel: 'Sofa & upholstery shampoo',
    accent: 'peach',
    eyebrow: 'Special service · Upholstery · Kochi',
    heroTitle: 'Sofa, couch & upholstery shampooing',
    heroSubtitle:
      '3M Scotchgard fabric cleaning and protection for sofas, armchairs, dining chairs, and upholstered headboards — with fabric code checking, colour safety testing, and humidity-aware drying.',
    heroImageUrl: '/guide-sofa-upholstery-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles technician in orange uniform — sofa and upholstery shampoo extraction Kochi',
    canonicalPath: '/guides/sofa-upholstery-shampoo-kochi',
    schemaServiceName: 'Sofa and upholstery shampoo Kochi',
    seo: {
      title: 'Sofa Shampoo Kochi | Upholstery Cleaning & Scotchgard | Tools & Bubbles',
      description:
        'Professional sofa and upholstery shampooing in Kochi. 3M Scotchgard fabric cleaner, stain removal, drying guide. Leather notes and fabric code checking. Book online.',
      keywords:
        'sofa shampoo Kochi, upholstery cleaning Ernakulam, couch cleaning Kerala, sofa cleaning Kakkanad, Scotchgard sofa cleaning, Tools and Bubbles sofa',
    },
    productCallout: [
      {
        label: 'Upholstery cleaner',
        name: '3M™ Scotchgard™ Spot Remover and Upholstery Cleaner',
        detail:
          'Aerosol foam · Removes oil- and water-based soil · No rinse required · Safe on most upholstery fabrics',
      },
      {
        label: 'Fabric protector (post-clean)',
        name: '3M™ Scotchgard™ Fabric & Carpet Cleaner 4107 + Fabric Protector',
        detail:
          'Anti-soiling barrier · Repels spills · Extends upholstery life by 2–3× · Does not change feel or breathability',
      },
    ],
    sections: [
      {
        title: 'When your sofa needs professional cleaning',
        paragraphs: [
          "In Kerala's humid climate, upholstered sofas absorb airborne dust, skin oils, and sweat during normal use. Post-monsoon damp and festive entertaining accelerate soiling. Visible soiling — discoloured armrests, embedded pet hair, or food stains in seat creases — responds well to professional extraction shampooing.",
          'Sofas also harbour dust mites, dander, and — where pets are involved — significant allergen loads. Professional shampooing combined with 3M Scotchgard protection significantly reduces re-soiling rate.',
        ],
      },
      {
        title: 'Fabric code checking — critical first step',
        tone: 'lavender',
        bullets: [
          'W code (water cleaning): Can be cleaned with water-based shampoo — most polyester, cotton, and nylon upholstery.',
          'S code (solvent only): Cannot be water-cleaned — dry-clean solvent only. We use dry-cleaning method for these. We do not apply water-based products to S-coded fabric.',
          'W/S code: Both methods safe — we choose based on soil type.',
          'X code (vacuum only): No chemical cleaning. We can vacuum and deodorise only.',
          'Not coded (unlabelled/vintage): We perform a colour fastness and moisture test in a hidden area before proceeding.',
        ],
      },
      {
        title: 'Our cleaning process',
        steps: [
          'Inspection and code check: Fabric label checked on the underside or back of cushion. Material and code noted.',
          'Cushion removal: All loose cushions removed for separate treatment. Fixed cushions cleaned in place.',
          'Pre-vacuum: Professional vacuum removes all dry soil and pet hair.',
          'Colour test: 3M Scotchgard solution applied to hidden area; waited 2 minutes to confirm colour stability.',
          'Shampoo application: 3M Scotchgard Spot Remover / Upholstery Cleaner applied to soiled areas. For full sofa clean, cleaning solution applied systematically in sections.',
          'Agitation and extraction: Soft upholstery brush agitates gently; soiled solution extracted by portable wet-dry vacuum or bonnet pad.',
          'Deodorisation: Enzyme-based deodoriser applied to neutralise pet or sweat odour.',
          'Scotchgard protection (if selected): Uniform protective spray applied and allowed to dry fully before use.',
        ],
      },
      {
        title: 'Leather sofas — important note',
        tone: 'lavender',
        noteBox: {
          type: 'tip',
          text: 'Leather (genuine, bonded, or PU) requires pH-specific leather cleaner and conditioner — NOT standard fabric shampoo. Mention at booking that your sofa is leather; our team brings appropriate leather care products.',
        },
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Remove all loose cushions, throw pillows, and blankets from the sofa.',
          'Clear the area around the sofa for access on all sides.',
          'Mention fabric type if known; check the underside label for the W/S/X code.',
          'Note any specific stains (wine, pet urine, oil, ink) so we can bring appropriate pre-treatments.',
          'Keep pets away from the sofa during and after cleaning until fully dry.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'mint',
        noteBox: {
          type: 'warning',
          text: 'Wet upholstery can cause slipping or falls. Keep children off the sofa until fully dry. 3M Scotchgard products are safe for children and pets once dry.',
        },
        bullets: [
          '3M Scotchgard Spot Remover is non-toxic at use concentration once the fabric is dry.',
          'Allow 2–4 hours drying time before placing children or pets on the sofa.',
          'In humid monsoon conditions, use fans or AC to speed drying and prevent mould in thick cushion foam.',
        ],
      },
      {
        title: 'After service — care and maintenance',
        tone: 'mint',
        bullets: [
          'Drying: 2–4 hours for standard synthetic fabrics; up to 6–8 hours for thick foam cushions in humid conditions.',
          'Do not place cushions back on the sofa until the base fabric is completely dry.',
          'Scotchgard-protected fabric repels liquid spills for up to 18 months — blot spills immediately rather than rubbing.',
          'Vacuum your sofa weekly to maintain Scotchgard performance and prevent new embedded soil.',
          'Avoid direct sunlight on cleaned upholstery for 24 hours after Scotchgard application.',
        ],
      },
    ],
    faq: [
      {
        q: 'How long does a full sofa clean take?',
        a: 'A standard 3-seater sofa takes 45–60 minutes. Additional chairs and chaise sections add 20–30 minutes each.',
      },
      {
        q: 'Will you remove pet urine odour?',
        a: 'Professional enzyme deodorisation significantly reduces pet urine odour. If urine has soaked into the foam core and dried, complete odour elimination may need multiple sessions — we set expectations on site.',
      },
      {
        q: 'My sofa fabric is velvet — can you clean it?',
        a: 'Velvet is W-coded but needs very gentle agitation to avoid pile crushing. We use a soft velvet brush and minimal moisture. Results are good for surface soil; heavy stains on velvet may not fully respond.',
      },
    ],
    amcText:
      'Twice-yearly sofa cleaning under our AMC maintains appearance, extends upholstery life by reducing grit abrasion, and keeps allergen loads low for family health. AMC includes a free Scotchgard reapplication visit annually. Available across Kochi, Edappally, Kakkanad, and Ernakulam.',
  },

  // ─── 7. KITCHEN / FRIDGE / OVEN ──────────────────────────────────────────
  {
    slug: 'kitchen-fridge-oven-cabinets-kochi',
    navLabel: 'Kitchen, fridge, oven & cabinets',
    accent: 'mint',
    eyebrow: 'Special service · Kitchen · Kochi',
    heroTitle: 'Kitchen deep clean — cabinets, fridge & oven',
    heroSubtitle:
      'Professional degreasing of modular kitchen surfaces, chimney facades, refrigerator interior, and oven/microwave cleaning — using food-safe 3M Scotch-Brite chemistry and Kärcher equipment.',
    heroImageUrl: '/guide-kitchen-deep-clean-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles team in orange uniforms — modular kitchen deep clean and degreasing Kochi',
    canonicalPath: '/guides/kitchen-fridge-oven-cabinets-kochi',
    schemaServiceName: 'Kitchen refrigerator oven and cabinet cleaning Kochi',
    seo: {
      title: 'Kitchen Deep Clean Kochi | Fridge, Oven & Cabinets | Tools & Bubbles',
      description:
        'Kitchen cabinet degreasing, refrigerator sanitisation, oven and microwave cleaning in Kochi using 3M Scotch-Brite products. Food-safe protocols. Prep checklist and after-service guide.',
      keywords:
        'kitchen deep cleaning Kochi, fridge cleaning Ernakulam, oven cleaning Kerala, kitchen cabinet degreasing Kochi, chimney cleaning Kakkanad, Tools and Bubbles kitchen',
    },
    productCallout: [
      {
        label: 'Degreaser',
        name: 'Scotch-Brite™ Kitchen Cleaner & Degreaser (3M India)',
        detail:
          'Breaks carbonised grease · Food-contact surface safe · Reduces cleaning time vs caustic cleaners',
      },
      {
        label: 'Surface disinfectant (post-clean)',
        name: '3M P2 General Purpose Disinfectant',
        detail: 'BKC-based · pH neutral · Applied on food-contact surfaces after degreasing and rinsing',
      },
    ],
    sections: [
      {
        title: 'What we clean in a full kitchen deep service',
        gridCards: [
          'Overhead & base cabinets — interior and facade',
          'Chimney / range hood facade & accessible grills',
          'Refrigerator interior, shelves & drawers',
          'Oven interior (walls, racks, door glass)',
          'Microwave interior and exterior',
          'Gas hob and burner area (exterior)',
          'Backsplash and counter surfaces',
          'Kitchen sink and surrounding tiles',
          'Cabinet handles and hinges',
          'Exterior of all appliances',
        ],
      },
      {
        title: 'How we perform the service',
        steps: [
          'Pre-service assessment: Supervisor walks through the kitchen, notes cabinet material (PVC, laminate, wood, steel), appliance types, and any damage pre-existing. You approve the scope before we start.',
          'Appliance isolation: All appliances are switched off and unplugged. Refrigerator is switched off and shelves removed for separate cleaning.',
          'Cabinet degreasing: Scotch-Brite Kitchen Cleaner & Degreaser applied to cabinet interiors and facades. Dwell time 5–10 minutes then wiped with appropriate Scotch-Brite pads matched to surface hardness.',
          'Oven / microwave interior: Oven must be cool before service. Degreaser applied inside; carbon deposits softened and removed. Oven racks are soaked and scrubbed separately.',
          'Refrigerator: Shelves and drawers cleaned separately with food-safe neutral cleaner. Interior walls wiped and deodorised. Compressor coils at back dusted if accessible.',
          'Gas hob area: Burner caps and tray areas soaked and scrubbed. Igniters are cleaned with dry brush — no water on electrical ignition components.',
          'Chimney facade: Exterior panels wiped; accessible mesh filters removed and degreased (if replacement not needed). Internal extraction mechanism is NOT part of a standard clean — requires separate chimney servicing.',
          'Final rinse & disinfection: All food-contact surfaces (shelves, racks, interior cabinet surfaces) rinsed with clean water. 3M P2 disinfectant applied to refrigerator interior and contact surfaces.',
        ],
      },
      {
        title: 'Before we arrive — your preparation checklist',
        bullets: [
          'Empty the refrigerator completely — place perishables in a cooler box during service. Remove all items from cabinets being cleaned.',
          'Turn the oven off at least 2 hours before service — it must be completely cool.',
          'Remove items from the gas hob and surrounding countertop.',
          'Inform us if cabinets are PVC-foil, real wood veneer, or lacquered — this determines the cleaning product and pad used.',
          'Mark surfaces you do not want wet (e.g. exposed plywood, MDF edges with swelling risk).',
          'Keep children out of the kitchen during the service.',
        ],
        noteBox: {
          type: 'warning',
          text: 'Do not operate the oven or hob for 2 hours after service — allow degreaser residual on hob area to be fully wiped and rinsed. Appliances inside the refrigerator can be turned back on 30 minutes after service.',
        },
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        bullets: [
          'Scotch-Brite Kitchen Cleaner & Degreaser is food-contact safe when rinsed — we rinse all food-contact surfaces before applying 3M P2 disinfectant.',
          'Keep children out of the kitchen during service and for 30 minutes after completion.',
          '3M P2 disinfectant is safe on refrigerator interior once dry (15 minutes). Wipe shelves with a damp cloth before placing food.',
          'Do not allow children near open degreaser containers — spills on skin should be washed with soap and water.',
        ],
      },
      {
        title: 'After service care',
        tone: 'mint',
        bullets: [
          'Wipe refrigerator shelves with a clean damp cloth before returning food.',
          'Do not switch on the refrigerator for 30 minutes to allow compressor area to reset (if unplugged during service).',
          'Run microwave empty on full power for 60 seconds after service to ensure no moisture remains.',
          'Gas hob: re-light each burner and check for even flame — inform us if any burner has abnormal flame after service.',
          'Degreased cabinet surfaces may appear noticeably brighter — apply a thin layer of cabinet-safe polish or wax within 48 hours if desired.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do you service the inside of the chimney exhaust?',
        a: 'Standard service covers the chimney facade and accessible mesh filters. Deep chimney duct and motor servicing is a separate specialist service that requires chimney disassembly — book via the enquiry form for a quote.',
      },
      {
        q: 'Can you clean just the refrigerator without the full kitchen?',
        a: 'Yes — individual appliance cleaning can be booked as add-ons. The minimum order for Special Services still applies ({amountMinOrder} total), so combining with additional services is most cost-effective.',
      },
    ],
    amcText:
      'Quarterly kitchen deep cleans under our AMC prevent carbonised grease from becoming irreversible and keep appliance efficiency up. AMC customers receive priority scheduling, free chimney filter inspection, and 15–20% savings. Ideal for families in Kochi who cook daily on gas hobs.',
  },

  // ─── 8. WARDROBE / SHOWCASE ──────────────────────────────────────────────
  {
    slug: 'wardrobe-showcase-interior-kochi',
    navLabel: 'Wardrobe & showcase interiors',
    accent: 'violet',
    eyebrow: 'Special service · Storage & display · Kochi',
    heroTitle: 'Wardrobe, showcase & shelf interior cleaning',
    heroSubtitle:
      'Interior dust removal, streak-free glass cleaning, veneer and laminate care, and hardware detailing for wardrobes, glass showcases, cutlery drawers, and display shelves.',
    heroImageUrl: '/images/hero-bedroom.png',
    heroImageAlt:
      'Tools & Bubbles — bedroom and wardrobe interior cleaning in Kochi (Indian professional team)',
    canonicalPath: '/guides/wardrobe-showcase-interior-kochi',
    schemaServiceName: 'Wardrobe showcase and shelf interior cleaning Kochi',
    seo: {
      title: 'Wardrobe Cleaning Kochi | Showcase & Shelf Interiors | Tools & Bubbles',
      description:
        'Interior cleaning of wardrobes, glass showcases, cutlery drawers, and display shelves in Kochi. Glass streak control, veneer care, hardware detailing. Book via Special Services.',
      keywords:
        'wardrobe cleaning Kochi, showcase cleaning Ernakulam, shelf cleaning Kerala, wardrobe interior cleaning Kochi, display cabinet cleaning, Tools and Bubbles wardrobe',
    },
    productCallout: [
      {
        label: 'General surface cleaner',
        name: '3M™ General Purpose Cleaner Concentrate',
        detail: 'Versatile · Safe on laminates, PVC, glass, metal · Diluted per surface type',
      },
      {
        label: 'Glass cleaner',
        name: '3M™ Glass Cleaner / Scotch-Brite glass wipes',
        detail: 'Streak-free · Ammonia-free option available · Safe on tinted and plain glass',
      },
    ],
    sections: [
      {
        title: 'What a wardrobe and showcase clean covers',
        gridCards: [
          'Interior shelf surfaces and base panels',
          'Glass doors and mirrors (streak-free)',
          'Drawer runners and channel tracks',
          'Wardrobe rods and pole mounts',
          'Handles, hinges, and pull knobs',
          'Display case interior glass shelves',
          'Cutlery tray interiors and dividers',
          'Cabinet top and sides (exterior)',
        ],
      },
      {
        title: 'How we perform the service',
        steps: [
          'Contents removal: Customer removes or shifts items section by section. We do not handle heirlooms or valuables unassisted unless specifically consented.',
          'Dry dust removal: HEPA vacuum removes loose dust, lint, and debris from shelves, corners, runners, and hinges.',
          'Interior surface clean: 3M General Purpose Cleaner applied to laminate, PVC, or wooden interior surfaces. Wiped dry with microfibre cloths.',
          'Glass cleaning: Showcase doors and interior glass shelves cleaned with 3M Glass Cleaner; streak-free finish using lint-free cloth.',
          'Hardware detailing: Handles, hinges, and runners wiped; metal hardware polished if stainless or chrome.',
          'Drawer runners: Tracks vacuumed and wiped to remove dust and fabric lint that impedes smooth operation.',
          'Final inspection: Supervisor checks all glass for streaks and all interior surfaces for residue under a torch.',
        ],
      },
      {
        title: 'Surface material guide',
        tone: 'lavender',
        bullets: [
          'Laminate / vinyl wrap: General purpose cleaner safe. Avoid prolonged water contact at edges.',
          'MDF or plywood (painted): Use minimal moisture; do not allow water to soak into edges or exposed MDF.',
          'Real wood / teak veneer: Specific wood-safe cleaner; minimal moisture; dry immediately.',
          'Toughened glass doors: Standard glass cleaner safe; remove frames before cleaning if possible.',
          'Powder-coated metal: Wipe with damp cloth and dry — do not use abrasive pads.',
          'Acrylic / perspex: Soft microfibre only; no solvent-based products.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Remove personal items (clothes, crockery, silverware, collectibles) from the sections to be cleaned.',
          'Jewellery, valuables, and heirlooms should be secured by you before our arrival.',
          'Indicate cabinet material if known — important for product selection.',
          'If showcasing antiques or collectibles, note which items are not to be moved.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'mint',
        noteBox: {
          type: 'info',
          text: '3M General Purpose Cleaner and glass cleaners used in this service are low-toxicity products at standard dilution. No special re-entry restriction required. Normal ventilation during service is recommended.',
        },
      },
    ],
    faq: [
      {
        q: 'Will you polish brass or silver items inside showcases?',
        a: 'We wipe exterior surfaces of decorative items with a dry or slightly damp cloth. Polishing precious metals (silver, brass antiques) requires explicit customer consent and specialist polish products — this can be added to the service.',
      },
      {
        q: 'Can you disassemble and clean a modular wardrobe?',
        a: 'We clean accessible interiors without disassembly. If panels need to be removed for thorough cleaning, we note this and discuss scope with you before proceeding.',
      },
    ],
    amcText:
      "Seasonal wardrobe cleaning (pre-Onam, pre-monsoon, post-monsoon) under our AMC keeps mould and dust at bay in Kerala's humid climate. Particularly recommended for large wardrobe systems in air-conditioned bedrooms that are sealed for most of the day.",
  },

  // ─── 9. PRESSURE WASHING ─────────────────────────────────────────────────
  {
    slug: 'pressure-washing-outdoor-kochi',
    navLabel: 'Pressure washing (outdoor)',
    accent: 'peach',
    eyebrow: 'Special service · Exteriors · Kochi',
    heroTitle: 'Pressure washing — terrace, porch & parking',
    heroSubtitle:
      'Professional Kärcher pressure washing removes algae, monsoon mud, and embedded stains from concrete, tile, and exterior hardscapes — with controlled pressure, drainage management, and plant protection.',
    heroImageUrl: '/images/addon-services/pressureWash.png',
    heroImageAlt:
      'Tools & Bubbles technician — outdoor pressure washing on modern terrace with glass pergola in Kochi Kerala',
    canonicalPath: '/guides/pressure-washing-outdoor-kochi',
    schemaServiceName: 'Pressure washing outdoor areas Kochi',
    seo: {
      title: 'Pressure Washing Kochi | Terrace, Porch & Parking | Tools & Bubbles',
      description:
        'Professional pressure washing in Kochi — terrace, car porch, parking bay, pergola, and exterior tiles. Kärcher equipment, algae removal, drainage and plant safety guide.',
      keywords:
        'pressure washing Kochi, terrace cleaning Ernakulam, car porch cleaning Kerala, outdoor floor cleaning Kochi, algae removal terrace Kakkanad, Tools and Bubbles pressure wash',
    },
    productCallout: [
      {
        label: 'Equipment',
        name: 'Kärcher Professional Electric Pressure Washer (K-series)',
        detail:
          '100–130 bar pressure · Adjustable lance · German engineering for residential and light commercial use',
      },
      {
        label: 'Cleaning chemistry',
        name: '3M™ 3-in-1 Floor Cleaner Concentrate 24 / Scotch-Brite Algae Remover',
        detail:
          'Pre-spray to loosen algae and embedded soil before pressure wash · pH appropriate for tile and concrete',
      },
    ],
    sections: [
      {
        title: 'Why outdoor surfaces need professional pressure washing',
        paragraphs: [
          "Kerala's monsoon brings months of rain, standing water, and high humidity — ideal conditions for green algae, black mould, and lichen growth on outdoor tiles, concrete, and brick. Once established, these biofilms are slippery hazards and cosmetically degrade the surface rapidly.",
          'Manual scrubbing with a broom and bucket removes only surface layers. A Kärcher professional pressure washer delivers water at 100–130 bar, penetrating biofilm growth and dislodging embedded particulate that has been compressed into concrete and grout over years.',
          'We use the right pressure for each surface — high pressure for concrete parking bays, medium for tiled terraces, low/fan mode for painted surfaces — and always test before treating a large area to ensure the surface is compatible.',
        ],
      },
      {
        title: 'How we perform the service',
        steps: [
          'Surface inspection: Identify material (concrete, vitrified tile, natural stone, painted surface) and check for loose tiles, cracked grout, or fragile fixtures that need to be protected.',
          'Plant and property protection: Garden beds and plants near the wash area are covered or shielded. Vehicles are moved or protected with sheeting. External electrical points are covered.',
          'Pre-treatment: 3M Floor Cleaner or algae remover applied as pre-spray and allowed to dwell 5–10 minutes to loosen biological growth before pressure application.',
          'Pressure washing: Starting at the highest point and working down to drainage outlets. Controlled lance pressure matched to surface type. Edge and corner detailing with rotary brush attachment.',
          'Debris removal: Post-wash debris swept toward drains and cleared.',
          'Final rinse: Clean water rinse ensures no pre-treatment chemistry residual on surface.',
          'Drainage check: Confirm drains are clear and water is flowing away from building foundations.',
        ],
      },
      {
        title: 'Surface compatibility guide',
        bullets: [
          'Concrete and rough-cast tile: High pressure (100–130 bar) safe for established concrete.',
          'Vitrified exterior tile: Medium pressure (80–100 bar) with fan nozzle. Avoid focusing pressure on grout lines.',
          'Natural stone (granite, kota): Medium pressure only; ensure grout is sealed before treatment.',
          'Painted concrete walls: Low pressure only — high pressure strips paint. We test before proceeding.',
          'Wooden decking: Low pressure with surface cleaner attachment only — high pressure raises grain.',
          'Brick and terracotta: Medium pressure; do not use rotating nozzle on porous brick.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Move vehicles away from the wash area or into a covered space.',
          'Clear outdoor furniture, potted plants, and decorative items from the wash zone.',
          'Identify drainage outlets so we can direct wash water appropriately.',
          'Mention any cracked tiles, loose pavers, or visible rust stains — these may need special treatment.',
          'Ensure outdoor power supply is accessible for the Kärcher machine.',
          'Inform neighbours if shared drainage or common areas may be affected.',
        ],
        noteBox: {
          type: 'warning',
          text: 'Never allow children or pets in the area during pressure washing — high-pressure water can cause injury. The wash area remains slippery for 30–60 minutes after service.',
        },
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        bullets: [
          'Pressure washing is a hazardous physical process — no bystanders within 5 metres during service.',
          'Post-wash surfaces are very wet and slippery — wait for full drying before allowing foot traffic.',
          'Pre-treatment chemicals are rinsed off during service — no residue hazard after final rinse.',
          'If a child contacts the pressure stream, seek immediate medical attention.',
        ],
      },
      {
        title: 'After service',
        tone: 'mint',
        bullets: [
          'Allow 1–2 hours drying before foot traffic on tiled surfaces; concrete may stay damp longer depending on drainage.',
          'Algae-treated surfaces may look slightly lighter or reveal original tile colour.',
          'Apply a concrete sealer or tile joint sealer after drying (24–48 hours) to prevent rapid biofilm return.',
          'Annual pressure washing before or after monsoon season maintains appearance and reduces slip risk year-round.',
        ],
      },
    ],
    faq: [
      {
        q: 'Will pressure washing remove oil stains from parking bay?',
        a: 'Fresh oil responds better than old set stains. We apply a degreaser pre-spray for oil areas. Old oxidised stains may lighten significantly but may not fully disappear in one session. A dedicated oil stain treatment can be added to the scope.',
      },
      {
        q: 'Can you wash an external building facade?',
        a: 'External wall washing (soft-wash) is available for ground-floor exterior walls and below window level. Above ground-floor requires scaffolding assessment. Book via enquiry for larger scope projects.',
      },
    ],
    amcText:
      'Annual or bi-annual pressure washing under our AMC prevents algae from re-establishing and maintains the aesthetic of your home exterior. Most popular in Kochi for monsoon post-cleaning (November–December) and pre-Onam preparation (August–September).',
  },

  // ─── 10. PEST CONTROL ────────────────────────────────────────────────────
  {
    slug: 'pest-control-home-kochi',
    navLabel: 'General pest control (home)',
    accent: 'rose',
    eyebrow: 'Special service · Pest control · Kochi',
    heroTitle: 'Cockroach, mosquito & general pest control',
    heroSubtitle:
      'IPM-based residential pest control using Shastra 2.5F (Deltamethrin) — broad-spectrum residual spray for cockroaches, mosquitoes, flies, ants, and crawling insects. Safe protocols for families and pets.',
    heroImageUrl: '/pest-control-technician-tools-bubbles.png',
    heroImageAlt:
      'Indian Tools & Bubbles pest technician in orange uniform — residual spray for cockroaches and mosquitoes in a Kochi home',
    canonicalPath: '/guides/pest-control-home-kochi',
    schemaServiceName: 'Residential pest control Kochi',
    seo: {
      title: 'Pest Control Kochi | Deltamethrin Shastra 2.5F | Tools & Bubbles',
      description:
        'Home pest control in Kochi using Shastra 2.5F Deltamethrin — cockroaches, mosquitoes, flies, ants, and crawling insects. IPM approach, child & pet safety guide, re-entry times. Book online.',
      keywords:
        'pest control Kochi, cockroach control Ernakulam, Deltamethrin spray Kerala, Shastra 2.5F pest control, mosquito treatment Kochi, home pest spray Kakkanad, Tools and Bubbles pest control',
    },
    productCallout: [
      {
        label: 'Primary insecticide',
        name: 'Shastra 2.5F — Deltamethrin 2.5% Flow (UPL India)',
        detail:
          'Broad-spectrum pyrethroid · CIB&RC registered · Residual effect 4–6 weeks · Cockroaches, mosquitoes, flies, ants, crawling insects',
      },
      {
        label: 'Rodent control',
        name: 'Professional rodenticide block bait (anticoagulant)',
        detail:
          'Tamper-resistant bait stations placed in concealed runways · Not accessible to children or pets inside station',
      },
    ],
    sections: [
      {
        title: 'About our product: Shastra 2.5F (Deltamethrin)',
        paragraphs: [
          'We use Shastra 2.5F — a professional-grade insecticide manufactured by UPL India (United Phosphorus Limited). The active ingredient is Deltamethrin 2.5% (25 g/litre), a synthetic pyrethroid derived from natural pyrethrin. It is one of the most widely used professional pest control actives in India and globally.',
          'Deltamethrin works on the nervous system of insects (sodium channel disruption), causing rapid knockdown and kill. It leaves a residual layer on treated surfaces that continues to kill insects that walk across it for 4–6 weeks under normal indoor conditions.',
        ],
        bullets: [
          'Targets: cockroaches, mosquitoes, flies, houseflies, ants, beetles, silverfish, and other crawling and flying household insects.',
          'Application: diluted in water and applied as a residual spray to skirting boards, wall–floor junctions, pipe runs, under sinks, behind appliances, and other harbourage surfaces.',
          'Not for use on: food surfaces, food-contact areas, open water bodies, or where fish or bees may be directly exposed.',
          'CIB&RC (Central Insecticides Board and Registration Committee) registered for indoor residential use in India.',
          'Shastra 2.5F by UPL India — available in 1-litre professional bottles; standard dilution 5–10 ml per litre of water.',
        ],
      },
      {
        title: 'IPM — our approach to pest control',
        paragraphs: [
          'We follow Integrated Pest Management (IPM) — the globally accepted standard that combines inspection, targeted treatment, and habitat modification. We do not broadcast-spray entire rooms; we apply Shastra 2.5F to specific harbourage surfaces where pests walk, rest, and breed.',
          'IPM minimises chemical exposure for your family while achieving better long-term pest reduction than non-targeted spraying.',
        ],
        bullets: [
          'Inspection first — identify pest species, entry points, runways, and harbourage before selecting treatment zones.',
          'Targeted residual spray — Shastra 2.5F applied to skirting boards, pipe runs, under furniture, false ceiling edges, and identified runways.',
          'Habitat advice — we recommend sealing entry gaps, fixing leaky pipes, and proper food storage to prevent re-infestation.',
          'Follow-up — a second visit at 14–21 days confirms residual effectiveness and treats any newly active zones.',
        ],
      },
      {
        title: 'How we perform the service — step by step',
        steps: [
          'Inspection & pest ID: Technician walks through all areas, identifies pest species present, notes infestation severity, and marks treatment zones. Photographs taken for the job report.',
          'PPE donning: Full PPE — chemical-resistant gloves, half-face respirator with organic vapour filter, spray suit, and closed-toe shoes.',
          'Shastra 2.5F dilution: Measured at 5–10 ml per litre of clean water in a calibrated knapsack sprayer. Concentration adjusted for pest type and severity per UPL label guidance.',
          'Residual spray application: Skirting boards at floor level, inside and behind kitchen cabinets, under sinks, around pipe penetrations, false ceiling gaps, furniture legs, and any identified runway or harbourage. Light mist — not heavy drench. Food-contact surfaces and open food areas are avoided completely.',
          'Crack and crevice injection: Pressurised application into wall cracks, cabinet hinges, gap behind kick plates, and electrical duct openings where insects harbour.',
          'Flying insect resting areas: For mosquitoes and flies — a light mist applied to the undersides of shelves, behind curtains, and interior corners where adults rest.',
          'Rodent bait stations (if booked): Tamper-resistant stations placed along wall edges, inside roof void access, and near identified runways. Dead rodents cleared at follow-up visit.',
          'Site clearance: Spray equipment is rinsed and stowed. Job completion report issued on-site with treated zones documented.',
        ],
      },
      {
        title: 'Before we arrive — your preparation checklist',
        bullets: [
          'Clear under sinks, behind kitchen appliances, and inside lower kitchen cabinets for treatment access.',
          'Remove or cover all exposed food, water, and food-preparation items. Cover cooking vessels and utensils.',
          'Move pet food bowls, water dishes, and pet bedding out of rooms to be treated.',
          'Cover fish tanks fully with a plastic sheet and switch off aerators — deltamethrin is highly toxic to fish and aquatic organisms.',
          'Remove pet birds from the treatment area or cover cages fully.',
          'Tell us which pests are visible and where — this helps us prioritise treatment zones.',
          'Mention if anyone is pregnant, asthmatic, on chemotherapy, or has skin sensitivities — we adjust technique accordingly.',
          'Switch off ceiling fans 15 minutes before service — fans disperse spray and reduce residual effectiveness.',
        ],
        noteBox: {
          type: 'warning',
          text: 'Deltamethrin is highly toxic to fish, aquatic insects, and bees. Never allow spray to reach aquariums, outdoor ponds, or exposed water bodies. All fish tanks must be sealed and aerators switched off before service begins.',
        },
      },
      {
        title: 'What happens when our team arrives',
        bullets: [
          'Technician arrives in a marked vehicle, shows ID and service order.',
          'Supervisor walks through the premises with you — confirms preparation, identifies treatment zones, and answers questions before spraying begins.',
          'All occupants, children, and pets vacate rooms as treatment proceeds room by room.',
          'Treatment typically takes 45–90 minutes for a standard 2–3 BHK home.',
          'After treatment, supervisor gives verbal briefing and leaves a written re-entry and after-care guide.',
        ],
      },
      {
        title: 'Child & family safety guide',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: '⚠ Keep all children and pets out of treated rooms during spraying and until the re-entry period has elapsed and treated surfaces are completely dry. Do not allow children to touch treated skirting boards or surfaces.',
        },
        bullets: [
          'Re-entry for adults (healthy): minimum 1–2 hours; open windows and ventilate fully.',
          'Re-entry for children: minimum 2 hours; ensure surfaces are dry and room is ventilated before children enter.',
          'Infants under 2 years: minimum 3 hours; clean any toys that may have contacted treated surfaces with a damp cloth.',
          'Pregnant women: minimum 2–3 hours; avoid handling treated surfaces unnecessarily for 6 hours after re-entry.',
          'Asthma / respiratory conditions: minimum 3 hours; ensure sustained cross ventilation.',
          'Cats: particularly sensitive to pyrethroids — minimum 3 hours out of treated areas; do not allow cats to lick treated surfaces.',
          'Dogs: minimum 2 hours; wash paws with soap and water if they walk on treated flooring before drying.',
          'Fish & aquatic pets: keep aquariums sealed until 3 hours after service; reconnect aerators only once ventilation is complete.',
          'If skin contact occurs: wash with soap and water. If irritation persists or product is ingested, contact a doctor immediately and provide the product name (Shastra 2.5F, Deltamethrin 2.5%).',
        ],
      },
      {
        title: 'After treatment — what to expect',
        tone: 'mint',
        bullets: [
          'Rapid knockdown: You may see dead or dying insects within minutes to hours of treatment — this is expected and shows the product is working.',
          'Residual effect: Shastra 2.5F residual layer continues killing insects that walk on treated surfaces for 4–6 weeks under normal indoor conditions.',
          'Do not mop treated skirting boards, under-sink areas, or pipe runs for 48 hours — this removes the residual layer.',
          'Do not spray commercial aerosol insecticides in treated areas for 2 weeks — they may dilute or interfere with the residual.',
          'For cockroaches: heavy infestation may require a follow-up spray at 21 days. Nymphs hatching from egg cases (oothecae) after the initial treatment will be killed by the residual.',
          'For mosquitoes: residual effect on resting surfaces lasts 4 weeks. Eliminate all standing water sources (flower pots, AC drain trays, roof gutters) to prevent re-breeding.',
          'Follow-up visit at 14–21 days to assess residual and re-treat if needed.',
        ],
      },
      {
        title: 'International standards and registration',
        bullets: [
          'CIB&RC (Central Insecticides Board and Registration Committee of India): Deltamethrin 2.5% EC/SC products are CIB-registered for indoor pest control in India.',
          'WHO Pesticide Evaluation Scheme: Deltamethrin is listed as a WHO Class II (moderately hazardous) insecticide — safe for professional application following label precautions.',
          'NPMA (National Pest Management Association): IPM principles followed — inspection → targeted treatment → monitoring → follow-up.',
          'UPL India Product Stewardship: Application rates and PPE requirements followed per official Shastra 2.5F label.',
          'All technicians trained in safe handling of Class II insecticides per Indian Insecticides Act, 1968 requirements.',
        ],
      },
    ],
    faq: [
      {
        q: 'What is Deltamethrin and is it safe for homes?',
        a: 'Deltamethrin is a synthetic pyrethroid — a laboratory version of the natural insecticide found in chrysanthemum flowers. It is highly effective against insects at very low doses and breaks down relatively quickly. When applied by trained technicians following label instructions, it is safe for occupied homes after the re-entry period.',
      },
      {
        q: 'How long does the residual spray last?',
        a: 'Shastra 2.5F provides a residual killing layer on treated surfaces for 4–6 weeks under normal indoor conditions. Heavy mopping of treated areas, excessive humidity, or direct sunlight exposure can reduce residual life. A follow-up spray at 3–6 weeks maintains protection.',
      },
      {
        q: 'My cat is very sensitive — is Deltamethrin safe for cats?',
        a: 'Cats metabolise pyrethroids more slowly than dogs or humans, making them more sensitive. The safe approach: keep cats out of treated areas for at least 3 hours, ensure all surfaces are dry before re-entry, and prevent cats from licking treated surfaces (skirting boards etc.) for 24 hours. Inform us if you have cats — we adjust spray zones accordingly.',
      },
      {
        q: 'What is the difference between this and bed bug treatment?',
        a: 'Bed bugs require Syngenta Acrenzel WDG (Clothianidin non-repellent neonicotinoid) applied specifically to mattress seams, headboard joints, and bed frame crevices. General pest spray with Deltamethrin is not an appropriate bed bug treatment. Always book the dedicated Bed Bug Treatment service if you suspect bed bugs.',
      },
    ],
    amcText:
      'Quarterly Pest Control AMC prevents seasonal cockroach surges (pre-monsoon and post-monsoon are peak in Kochi) and keeps mosquito populations under control year-round. AMC includes 4 spray visits per year, rodent monitoring station service, a free inspection visit, and priority call-out within 48 hours. Available as a standalone pest AMC or combined with cleaning AMC for maximum value.',
  },

  // ─── 11. AC DUCT CLEANING ────────────────────────────────────────────────
  {
    slug: 'ac-duct-hvac-cleaning-kochi',
    navLabel: 'AC grills & duct access cleaning',
    accent: 'mint',
    eyebrow: 'Special service · HVAC · Kochi',
    heroTitle: 'AC grills, filters & reachable duct cleaning',
    heroSubtitle:
      'Cleaning of accessible AC supply/return grills, washable filters, drain trays, coil surface (blower units), and near-outlet duct sections — improving airflow and indoor air quality.',
    heroImageUrl: '/images/3m_cleaning_in_kochi.png',
    heroImageAlt:
      'Tools & Bubbles Indian technicians — professional home cleaning including AC area and surfaces in Kochi',
    canonicalPath: '/guides/ac-duct-hvac-cleaning-kochi',
    schemaServiceName: 'AC duct and HVAC surface cleaning Kochi',
    seo: {
      title: 'AC Duct Cleaning Kochi | Grills, Filters & Coil | Tools & Bubbles',
      description:
        'Residential AC grill, filter, drain tray, and accessible duct cleaning in Kochi. 3M P2 fogging for duct misting, coil cleaning, and indoor air quality improvement. Book online.',
      keywords:
        'AC duct cleaning Kochi, AC grill cleaning Ernakulam, HVAC cleaning Kerala, split AC deep clean Kochi, AC filter service Kakkanad, Tools and Bubbles AC',
    },
    productCallout: [
      {
        label: 'Duct and coil disinfection',
        name: '3M P2 General Purpose Disinfectant (ULV misting)',
        detail:
          'Applied via ULV fogger into accessible duct mouths and over coil surfaces · Anti-bacterial and anti-fungal · pH neutral',
      },
      {
        label: 'Coil cleaner',
        name: 'Professional AC coil cleaner (no-rinse foam)',
        detail:
          'Drip-through formula dissolves grime and biological growth on evaporator coil · Compatible with aluminium fins',
      },
    ],
    sections: [
      {
        title: 'What we clean in an AC and duct service',
        gridCards: [
          'Supply air grills and louvres',
          'Return air filter panels',
          'Washable filters (wash and dry)',
          'Evaporator coil face (accessible)',
          'Condensate drain tray and outlet',
          'Blower wheel (accessible models)',
          'Duct outlet and near-mouth sections',
          'Ceiling cassette panels',
          'ULV disinfection mist into ducts',
        ],
      },
      {
        title: 'Why AC cleaning matters — and what we cannot do',
        paragraphs: [
          "In Kerala's humid climate, AC evaporator coils and drain trays accumulate biofilm — a combination of bacteria, mould spores, and algae — within weeks of installation if not maintained. This biofilm causes the distinctive 'dirty sock' AC smell, may worsen indoor air quality, and blocks condensate drainage leading to water drips.",
          'We clean what is safely accessible without demolition — coil face, drain tray, grills, filters, and near-mouth duct sections. We DO NOT have robotic brushing equipment for sealed long-run ducts — that requires specialist duct contractor equipment.',
        ],
        noteBox: {
          type: 'info',
          text: 'If your AC system has sealed long-run ducts (common in central AC systems for larger homes or offices), those require a specialist duct-cleaning contractor with robotic brush equipment. We will flag this on-site if applicable.',
        },
      },
      {
        title: 'How we perform the service',
        steps: [
          'System switch off: AC and HVAC units switched off and power isolated at DB (distribution board).',
          'Grill and filter removal: Grills and filter panels removed for separate washing with water and neutral cleaner.',
          'Coil surface cleaning: No-rinse coil cleaner foam applied to evaporator coil face. Foam penetrates and drips through fins, carrying grime to the drain tray.',
          'Drain tray cleaning: Condensate drain tray scrubbed and blockage in drain outlet cleared with flexible rod tool. Algaecide tablet placed in tray to prevent rapid re-growth.',
          'Blower wheel (if accessible): Soft brush and vacuum remove accumulated dust from wheel blades.',
          'ULV duct misting: 3M P2 disinfectant applied via small ULV fogger into accessible supply duct mouths — settles on duct interior surfaces within accessible distance.',
          'Grill reinstallation: Washed and dried grills and filter panels replaced.',
          'Operational check: System powered on; supervisor checks for condensate drainage and confirms no unusual noise.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Switch off the AC and thermostat 30 minutes before our arrival so the coil is not icy or sweating.',
          'Clear the area below ceiling cassettes or near wall-mount units for technician access.',
          'Identify the location of the DB (circuit breaker) for the AC circuits — we will need to isolate power.',
          'Mention the AC brand, model, and age if known — this helps us bring appropriate coil cleaner and brushes.',
          'If you have central ducted AC with long sealed runs, inform us — this changes the scope and equipment needed.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Keep children away from the area while AC power is isolated and technicians work with coil cleaner. Coil cleaning foam can drip from the drain tray — protect flooring with old towels.',
        },
        bullets: [
          '3M P2 used for duct misting is pH neutral and non-toxic at standard dilution. Vacate the room for 30 minutes after ULV misting.',
          'Do not run the AC for 30 minutes after service — allow coil cleaner drip cycle to complete.',
          'Children with asthma benefit most from regular AC maintenance — biofilm in coils is a documented asthma trigger.',
        ],
      },
      {
        title: 'After service',
        tone: 'mint',
        bullets: [
          'Wait 30 minutes before switching on the AC to allow coil to drain and surface to stabilise.',
          'First 30 minutes of operation may show slightly increased water drip from drain tray — this is normal as residual foam exits.',
          'Cooling efficiency should noticeably improve within 1–2 hours of operation.',
          'Bad odour from vents should reduce significantly or disappear within 24 hours.',
          'Clean filters every 2–3 weeks by light brushing/vacuuming to maintain service life.',
        ],
      },
    ],
    faq: [
      {
        q: 'Will cleaning my AC fix a bad smell from the vents?',
        a: "AC odour (musty or 'dirty sock' smell) is almost always caused by biofilm on the evaporator coil or condensate drain. Cleaning relieves this significantly. If the smell persists after cleaning, it may indicate dead pests inside inaccessible duct runs — requires further investigation.",
      },
      {
        q: 'How often should residential AC units be cleaned?',
        a: "Evaporator coil and drain tray cleaning every 6–12 months is recommended in Kerala's humid climate. Filter cleaning every 2–4 weeks is a basic user maintenance task. Annual full service prevents major build-up.",
      },
    ],
    amcText:
      'Bi-annual AC maintenance under our AMC keeps coils biofilm-free, drain trays clear, and filters clean — maintaining cooling efficiency and indoor air quality. AMC includes two service visits per year and a free ULV disinfection misting session. Particularly popular in homes with central AC systems.',
  },

  // ─── 12. FURNITURE ARRANGEMENT ───────────────────────────────────────────
  {
    slug: 'furniture-arrangement-staging-kochi',
    navLabel: 'Furniture arrangement & staging',
    accent: 'violet',
    eyebrow: 'Special service · Moving help · Kochi',
    heroTitle: 'In-home furniture moving & arrangement',
    heroSubtitle:
      'Careful furniture repositioning for events, paint cycles, renovation clearance, or new room layouts — floor protection, team lifts, and scratch-prevention protocols.',
    heroImageUrl: '/images/tools-bubbles-living-room-cleaning.png',
    heroImageAlt: 'Tools & Bubbles Indian team — living room cleaning and furniture care in Kochi',
    canonicalPath: '/guides/furniture-arrangement-staging-kochi',
    schemaServiceName: 'Furniture arrangement and staging assistance Kochi',
    seo: {
      title: 'Furniture Arrangement Kochi | In-Home Moving Help | Tools & Bubbles',
      description:
        'In-home furniture rearrangement and staging in Kochi — floor protection, team lifts, heavy furniture moving, and scratch prevention. Complements cleaning services. Book online.',
      keywords:
        'furniture arrangement Kochi, furniture rearrangement Ernakulam, in-home moving help Kerala, room staging Kochi, furniture shifting service, Tools and Bubbles furniture',
    },
    sections: [
      {
        title: 'What this service covers',
        gridCards: [
          'Bedroom furniture repositioning',
          'Living room layout changes',
          'Heavy sofa and cabinet shifting',
          'Pre-painting furniture clearance',
          'Post-renovation furniture placement',
          'Pre-event room configuration',
          'Dining table and chair repositioning',
          'Appliance shifting for floor cleaning',
        ],
      },
      {
        title: 'How we perform the service',
        steps: [
          'Walk-through and plan: Customer describes desired layout (or shows a sketch). Team assesses weight, floor type, doorway widths, and staircase clearances.',
          'Floor protection: Furniture sliders (felt pads or plastic caps) placed under legs. Rubber mats laid for pivot areas on tiled floors.',
          'Team lift protocol: Heavy items (solid wood beds, granite-top tables, refrigerators) are lifted by 2–4 technicians simultaneously — not dragged. No single-person lifts for items over 50 kg.',
          'Tight clearances: For items passing through doorways, glass toppers and loose parts removed first and carried separately.',
          'Placement and levelling: Furniture placed in new position; legs and feet checked on uneven flooring.',
          'Connected items: Wall-mounted TVs and fixed shelving are NOT moved. Gas connections are NOT disconnected. We note scope limits before beginning.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Describe the heaviest items (solid wood, glass tops, stone tops, built-in appliances) when booking.',
          'Photograph the current layout and target layout if possible — share via WhatsApp so we can plan the team size.',
          'Remove fragile and decorative items from tops and shelves of furniture to be moved.',
          'Secure aquariums, mounted TVs, and wall units — these are not within scope unless specifically agreed.',
          'For staircases: measure the narrowest point and door frame width for large items.',
          'Ensure sufficient space in the destination position for the furniture.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'Keep children away from the furniture movement area. Heavy items being carried or slid create crush and slip hazards. This is particularly important on stairs.',
        },
        bullets: [
          'No unsupervised children in rooms where heavy furniture is being moved.',
          'All technicians wear closed-toe shoes; no furniture is moved without adequate team size.',
          'Floor sliders used to protect tile and marble — no dragging that creates debris or tile damage.',
        ],
      },
      {
        title: 'What we do not include',
        bullets: [
          'Wall TV removal or remounting.',
          'Gas line or electrical disconnection.',
          'Dismantling of hydraulic storage beds (unless explicitly agreed).',
          'Long-distance furniture transport (this is in-home only, not inter-city moving).',
          'Installation of new shelving or fixtures.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do you disassemble flat-pack beds?',
        a: "Standard knock-down beds (Allen key assembly) yes, where it helps the move. Complex hydraulic storage beds require the manufacturer's guidance and dedicated tools — we assess on site.",
      },
      {
        q: 'Can you combine furniture moving with floor scrubbing in the same booking?',
        a: 'Yes — this is the most popular combination. We move the furniture, scrub the floor, and replace furniture once the floor is dry. Book both as add-ons under Special Services.',
      },
    ],
    amcText:
      'If you rearrange rooms seasonally (pre-monsoon, Onam, or after renovation), our AMC can include a furniture arrangement slot alongside each scheduled cleaning visit. Saves time and ensures team familiarity with your home layout.',
  },

  // ─── 13. GENERAL CLEANING ADD-ON ─────────────────────────────────────────
  {
    slug: 'general-cleaning-add-on-kochi',
    navLabel: 'General / extra cleaning (add-on)',
    accent: 'peach',
    eyebrow: 'Special service · Add-on hours · Kochi',
    heroTitle: 'Extra scrubbing, mopping & general cleaning',
    heroSubtitle:
      'Per-square-foot or time-boxed supplemental cleaning when your main package needs more coverage — additional rooms, post-construction detailing, or second-pass deep cleaning.',
    heroImageUrl: '/images/tools-bubbles-team-deep-clean.png',
    heroImageAlt:
      'Tools & Bubbles Indian cleaning crew — deep clean and general housekeeping in Kochi Kerala',
    canonicalPath: '/guides/general-cleaning-add-on-kochi',
    schemaServiceName: 'General add-on cleaning Kochi',
    seo: {
      title: 'Extra General Cleaning Kochi | Add-On Sq Ft | Tools & Bubbles',
      description:
        'Additional general cleaning as a Special Service add-on in Kochi. Per-sq-ft pricing, how to combine with other add-ons, and the minimum order guide. Book online.',
      keywords:
        'extra cleaning Kochi, add on cleaning Ernakulam, per sq ft cleaning Kerala, deep clean add on Kochi, supplemental cleaning, Tools and Bubbles general',
    },
    productCallout: [
      {
        label: 'Floor cleaning',
        name: '3M™ Neutral Cleaner Concentrate 3 (3M India)',
        detail: 'pH-neutral · Safe on all floor types · No rinse required · Used with Scotch-Brite mops',
      },
      {
        label: 'Surface wipe-down',
        name: '3M P2 General Purpose Disinfectant',
        detail: 'BKC-based surface cleaner for high-touch areas — switches, handles, bannisters',
      },
    ],
    sections: [
      {
        title: 'When this add-on is the right choice',
        paragraphs: [
          'Your existing deep clean package covers a defined scope (e.g. 2BHK standard rooms). If you have additional areas — a guest room, extended balcony, staff quarters, store room, or an unusual layout — the General Cleaning add-on extends the coverage using the same team and visit.',
          'It is also the right add-on for post-construction detailing (dust control passes, grout wiping, glass sill cleaning), or for a second mopping and surface-wipe pass after specialist treatments (post-fumigation, post-painting, post-renovation).',
        ],
      },
      {
        title: 'What a general cleaning add-on typically includes',
        bullets: [
          'Floor mopping and skirting wipe-down in additional rooms.',
          'Fan blade cleaning (ceiling fans).',
          'Balcony sweeping and wet wipe.',
          'Window sill and ledge cleaning (interior side).',
          'Light fixture and switch plate dusting.',
          'High-surface dusting (above wardrobes, door frames).',
          'Additional bathroom surface clean (if separate from main scope).',
          'Post-construction dust removal pass — walls, floors, and grilles.',
        ],
      },
      {
        title: 'How per sq ft pricing works with minimum order',
        tone: 'lavender',
        paragraphs: [
          'General cleaning add-ons are priced per square foot (as displayed in our current catalogue). The Special Services booking requires a minimum cart value of {amountMinOrder}. If your general cleaning add-on alone does not reach this, combine it with other special service add-ons (mattress cleaning, sofa shampooing, etc.) to reach the minimum efficiently.',
          'Our booking form shows the running total as you select add-ons — a progress bar guides you to the minimum order threshold.',
        ],
        noteBox: {
          type: 'info',
          text: 'Admin override: An admin can enable bookings below {amountMinOrder} minimum for special circumstances (corporate account, promotional). This does not apply automatically to any booking.',
        },
      },
      {
        title: 'Products used — 3M cleaning chemistry',
        tone: 'mint',
        bullets: [
          '3M Neutral Cleaner Concentrate 3: Diluted pH-neutral floor cleaner for all floor types. Safe on ceramic, vitrified, marble, granite, and kota stone. Used with Scotch-Brite string mop or flat mop.',
          '3M P2 Disinfectant: Applied to high-touch surfaces (switches, handles, door knobs) as part of disinfection pass. Wipe-on application.',
          'Scotch-Brite cleaning pads (3M India): Various grades for different surface hardness — no abrasive used on polished or sensitive surfaces.',
          'Kärcher equipment: Where applicable, Kärcher steam or wet-dry vacuum used for grout lines, window sills, and hard-to-reach ledges.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Clear the areas to be cleaned of small objects, mats, and furniture on the floor where possible.',
          'Mention post-construction specifics: plaster dust, tile grout residue, or cement marks on windows.',
          'Ensure access to all rooms and areas in scope.',
          'Switch off ceiling fans 30 minutes before arrival if fan blade cleaning is in scope.',
        ],
      },
      {
        title: 'Child & family safety',
        tone: 'lavender',
        bullets: [
          'Wet floors present a slip hazard during and after mopping. Keep children off cleaned floors until dry (15–20 minutes).',
          '3M Neutral Cleaner and P2 are safe when dry — no re-entry restriction beyond drying time.',
          'Ensure cross ventilation (open windows or fans) during and after cleaning for fresh air.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is this the same as a full deep clean package?',
        a: 'No. Deep clean packages are structured room lists with defined inclusions. This add-on extends the labour and coverage of an existing booking — not a replacement for a full package.',
      },
      {
        q: 'Can I book general cleaning add-on without any other special service?',
        a: 'Yes. You can book it standalone under Special Services as long as the minimum order of {amountMinOrder} is met. If the add-on value alone is less than {amountMinOrder}, combine with other add-ons.',
      },
    ],
    amcText:
      'Regular scheduled add-on cleaning visits under our AMC supplement your main deep clean frequency. Popular for homes where certain areas (balcony, store room, guest room) are not covered in the primary booking. AMC pricing is discounted 15–20% versus one-off add-on bookings.',
  },
  // ─── 14. WASTE REMOVAL ────────────────────────────────────────────────────
  {
    slug: 'waste-removal-service-kochi',
    navLabel: 'Waste removal service',
    accent: 'peach',
    eyebrow: 'Special service · Disposal support · Kochi',
    heroTitle: 'Waste removal service for homes and workplaces',
    heroSubtitle:
      'Safe bagging, segregation support, loading, and authorised disposal coordination for post-cleaning waste generated during deep cleaning, renovation handover, and move-out projects in Kochi.',
    heroImageUrl: '/guide-waste-removal-tools-bubbles.png',
    heroImageAlt:
      'Tools & Bubbles technician in orange uniform loading sealed waste disposal bags into a pickup truck after post-cleaning handover in Kochi',
    canonicalPath: '/guides/waste-removal-service-kochi',
    schemaServiceName: 'Waste removal and post-cleaning disposal support Kochi',
    seo: {
      title: 'Waste Removal Service Kochi | Post-Cleaning Disposal | Tools & Bubbles',
      description:
        'Waste removal service in Kochi for homes, offices, and move-out projects. Segregation support, safe handling, loading, and authorised disposal coordination. Book via Special Services.',
      keywords:
        'waste removal service Kochi, post cleaning waste disposal Ernakulam, debris bag removal Kerala, move out waste pickup Kochi, Tools and Bubbles waste service',
    },
    sections: [
      {
        title: 'When to use this add-on',
        paragraphs: [
          'Waste removal is ideal after deep cleaning, tenant move-out, pantry cleanouts, and light renovation handovers where multiple bags of waste remain on site.',
          'Our team supports collection, sorting guidance, safe bagging, and loading so your premises can be handed over clean and clutter-free on the same day.',
        ],
        gridCards: [
          'Post deep-cleaning bag disposal',
          'Move-out and handover cleanups',
          'Office pantry and store-room clear-outs',
          'General non-hazardous waste lifting support',
        ],
      },
      {
        title: 'How the service is executed',
        steps: [
          'Pre-check: We review waste volume, floor access, lift availability, and building movement restrictions.',
          'Segregation support: Team separates dry waste, wet waste, recyclable material, and non-recyclables as per local municipal practice.',
          'Safe bagging and sealing: Waste is packed in strong disposal bags and labelled where needed.',
          'Loading and movement: Bags are moved through approved pathways to avoid spills in common areas.',
          'Disposal coordination: Waste is handed to authorised collection points or approved disposal channels based on category.',
          'Final area check: Service zone is left broom-clean and ready for normal use.',
        ],
      },
      {
        title: 'Safety and exclusions',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'This add-on excludes biomedical waste, chemical waste, paint thinner drums, and legally restricted hazardous items unless separately approved in writing.',
        },
        bullets: [
          'Sharp objects (glass shards, blades, nails) must be declared before lifting.',
          'Residents should keep children and pets away from staging and loading zones during movement.',
          'Very bulky demolition debris may require separate vehicle and labour planning.',
        ],
      },
      {
        title: 'Before we arrive',
        bullets: [
          'Confirm whether waste is from household, office, or renovation clean-up.',
          'Share approximate bag count or photos for better manpower planning.',
          'Confirm lift access, parking slot, and building disposal timing window.',
          'Inform us if any items must be retained and should not be disposed.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do you remove renovation rubble and construction debris?',
        a: 'Yes for light-to-moderate volumes. Large civil debris volume may need separate quotation based on weight, lift constraints, and transport access.',
      },
      {
        q: 'Can I combine waste removal with deep cleaning in one visit?',
        a: 'Yes. This is the most common use case. We usually schedule waste removal at the end of the cleaning workflow for faster handover.',
      },
    ],
    amcText:
      'For offices and managed properties, AMC customers can schedule periodic disposal support visits to keep utility rooms and storage spaces uncluttered year-round.',
  },

  // ─── 15. HIGH-LEVEL CLEANING (ABOVE 5M) ─────────────────────────────────
  {
    slug: 'high-level-cleaning-scaffolding-kochi',
    navLabel: 'High-level cleaning above 5m',
    accent: 'violet',
    eyebrow: 'Special service · Height access · Kochi',
    heroTitle: 'High-level cleaning above 5 meters (with scaffolding)',
    heroSubtitle:
      'Professional high-access cleaning for atriums, facades, glass, ledges, and hard-to-reach structures using scaffolding, trained crew, PPE, and controlled safety protocols.',
    heroImageUrl: '/guide-high-level-cleaning-tools-bubbles.png',
    heroImageAlt:
      'Tools & Bubbles high-access cleaning team on certified scaffolding with helmets and harnesses cleaning elevated glass and beams in Kochi',
    canonicalPath: '/guides/high-level-cleaning-scaffolding-kochi',
    schemaServiceName: 'High-level cleaning above 5 meters with scaffolding Kochi',
    seo: {
      title: 'High-Level Cleaning Kochi | Above 5m with Scaffolding | Tools & Bubbles',
      description:
        'High-level cleaning above 5 meters in Kochi with scaffolding and safety supervision. Ideal for atriums, facades, high glass, and elevated dust removal in villas, offices, and commercial spaces.',
      keywords:
        'high level cleaning Kochi, scaffolding cleaning service Ernakulam, cleaning above 5 meter Kerala, facade high access cleaning Kochi, atrium glass cleaning Kochi',
    },
    sections: [
      {
        title: 'Where this service helps',
        paragraphs: [
          'This service is designed for spaces where normal ladders are not safe or sufficient, especially for heights above 5 meters.',
          'Typical scope includes atrium glass cleaning, exterior wall/facade dust removal, high-beam cobweb removal, elevated lighting area cleaning, and hard-to-reach structural surfaces.',
        ],
        gridCards: [
          'Villas with double-height living areas',
          'Office lobby atriums and glass fronts',
          'Commercial facades and signage zones',
          'Industrial sheds with high rafters',
        ],
      },
      {
        title: 'Execution and safety workflow',
        steps: [
          'Site survey: Supervisor checks working height, floor bearing, access points, and hazard zones.',
          'Method statement: Team finalises scaffold layout, cleaning tools, and sequence before work begins.',
          'PPE and setup: Crew uses helmets, gloves, non-slip footwear, and other required protective gear.',
          'Scaffold erection and stability check: Structure is assembled and verified before climbing operations.',
          'Cleaning execution: Elevated surfaces are cleaned section-by-section with controlled debris handling.',
          'Dismantling and handover: Scaffold is removed and site is restored after final quality walkthrough.',
        ],
      },
      {
        title: 'Customer preparation checklist',
        bullets: [
          'Provide clear access to work zones and keep floor area free of movable furniture.',
          'Mark sensitive items below the work area (electronics, décor, fragile items).',
          'Share building rules for scaffold timings, loading entry, and noise windows.',
          'Coordinate with facility/security team if work is in a shared commercial property.',
        ],
      },
      {
        title: 'Important safety notes',
        tone: 'lavender',
        noteBox: {
          type: 'warning',
          text: 'For work above 5 meters, scaffolding and controlled access are mandatory. We may reschedule if weather, floor condition, or access risk makes work unsafe.',
        },
        bullets: [
          'Children, visitors, and occupants must stay outside barricaded work zones.',
          'No loose overhead movement is allowed below active scaffold sections.',
          'Wet exterior work is paused during heavy rain or strong wind conditions.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can this be done without scaffolding to reduce cost?',
        a: 'For most jobs above 5 meters, no. Scaffolding is used to maintain worker safety and cleaning quality. We only propose alternate access when safety standards allow it.',
      },
      {
        q: 'Do you clean both interior and exterior high surfaces?',
        a: 'Yes. After survey, we can include interior atriums, stair voids, high glass, and external facade zones under one scoped service plan.',
      },
    ],
    amcText:
      'High-access AMC plans are available for hotels, offices, and premium residences that need periodic high-level dust and glass maintenance at safe intervals.',
  },
];

const BY_SLUG: Record<string, SpecialServiceGuideDef> = Object.fromEntries(
  SPECIAL_SERVICE_GUIDES.map((g) => [g.slug, g]),
);

export function getSpecialGuideBySlug(slug: string): SpecialServiceGuideDef | null {
  return BY_SLUG[slug] ?? null;
}

export function isRegisteredSpecialGuideSlug(slug: string): boolean {
  return slug in BY_SLUG;
}

export const SPECIAL_GUIDE_NAV_ITEMS: { label: string; href: string }[] = [...SPECIAL_SERVICE_GUIDES]
  .sort((a, b) => a.navLabel.localeCompare(b.navLabel))
  .map((g) => ({ label: g.navLabel, href: g.canonicalPath }));

export function getAllDynamicGuideCanonicalPaths(): string[] {
  return SPECIAL_SERVICE_GUIDES.map((g) => g.canonicalPath);
}

/**
 * Map add-on display name → guide URL. First matching rule wins.
 */
export function matchAddonNameToGuideHref(name: string): string | null {
  const n = name.toLowerCase();

  if (n.includes('bed bug')) return '/bed-bug-treatment-kochi';
  if (
    (n.includes('flies') || n.includes('flying insect')) &&
    (n.includes('pest') || n.includes('ulv') || n.includes('spray'))
  ) {
    return '/guides/pest-control-home-kochi';
  }
  if (
    n.includes('ulv') ||
    n.includes('fogging') ||
    n.includes('antiviral') ||
    n.includes('anti viral') ||
    (n.includes('bacterial') && n.includes('fungal')) ||
    (n.includes('anti') && n.includes('viral')) ||
    (n.includes('disinfection') && !n.includes('toilet'))
  ) {
    return '/ulv-fogging-disinfection-kochi';
  }

  if (n.includes('toilet') && (n.includes('acid') || n.includes('descal'))) {
    return '/guides/toilet-acid-wash-kochi';
  }
  if (n.includes('water tank') || n.includes('drainage') || n.includes('drain line')) {
    return '/guides/water-tank-drainage-cleaning-kochi';
  }
  if (
    (n.includes('floor') && (n.includes('scrub') || n.includes('scrubber'))) ||
    n.includes('disk machine') ||
    n.includes('single door disk') ||
    (n.includes('deep cleaning') && n.includes('disk'))
  ) {
    return '/guides/floor-scrubbing-machine-kochi';
  }
  if (n.includes('carpet') || n.includes('rug shampoo')) {
    return '/guides/carpet-shampoo-service-kochi';
  }
  if (n.includes('mattress')) {
    return '/guides/mattress-cleaning-sanitization-kochi';
  }
  if (
    n.includes('sofa') ||
    n.includes('upholstery') ||
    n.includes('couch') ||
    (n.includes('shampoo') && n.includes('fabric'))
  ) {
    return '/guides/sofa-upholstery-shampoo-kochi';
  }
  if (n.includes('refrigerator') || n.includes('fridge')) {
    return '/guides/kitchen-fridge-oven-cabinets-kochi';
  }
  if (n.includes('oven') || n.includes('microwave')) {
    return '/guides/kitchen-fridge-oven-cabinets-kochi';
  }
  if (
    n.includes('kitchen') ||
    n.includes('chimney') ||
    (n.includes('cabinet') && (n.includes('kitchen') || n.includes('modular')))
  ) {
    return '/guides/kitchen-fridge-oven-cabinets-kochi';
  }
  if (
    n.includes('wardrobe') ||
    n.includes('showcase') ||
    n.includes('cutlery') ||
    (n.includes('shelf') && (n.includes('clean') || n.includes('interior')))
  ) {
    return '/guides/wardrobe-showcase-interior-kochi';
  }
  if (
    n.includes('pressure wash') ||
    n.includes('car porch') ||
    n.includes('terrace') ||
    n.includes('pergola') ||
    (n.includes('parking') && n.includes('wash'))
  ) {
    return '/guides/pressure-washing-outdoor-kochi';
  }
  if (
    n.includes('cockroach') ||
    n.includes('rat bait') ||
    n.includes('glue board') ||
    n.includes('pest control') ||
    n.includes('flies') ||
    (n.includes('insect') && n.includes('control')) ||
    hasWord(n, 'rat') ||
    hasWord(n, 'pest')
  ) {
    return '/guides/pest-control-home-kochi';
  }
  if (
    n.includes('hvac') ||
    n.includes('duct cleaning') ||
    n.includes('ac duct') ||
    (n.includes('duct') && (n.includes('ac') || n.includes('clean')))
  ) {
    return '/guides/ac-duct-hvac-cleaning-kochi';
  }
  if (n.includes('furniture') && (n.includes('arrang') || n.includes('mov') || n.includes('shift'))) {
    return '/guides/furniture-arrangement-staging-kochi';
  }
  if (
    n.includes('waste removal') ||
    (n.includes('waste') && (n.includes('remove') || n.includes('disposal')))
  ) {
    return '/guides/waste-removal-service-kochi';
  }
  if (
    (n.includes('high') && n.includes('level') && n.includes('clean')) ||
    (n.includes('above') && (n.includes('5 meter') || n.includes('5m'))) ||
    (n.includes('scaffold') && n.includes('clean'))
  ) {
    return '/guides/high-level-cleaning-scaffolding-kochi';
  }
  if (
    n.includes('general cleaning') ||
    (n.includes('per sq') && n.includes('ft')) ||
    n.includes('per sqft') ||
    (n.includes('mopping') && n.includes('add')) ||
    (n.includes('brushing') && n.includes('add'))
  ) {
    return '/guides/general-cleaning-add-on-kochi';
  }

  return null;
}
